import {
  TextField,
  Typography,
  Button,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  AlertTitle,
  MenuItem,
} from '@mui/material'
import './LineEdit.css'
import { quoteLineType } from '../../types'
import { ArrowForward } from '@mui/icons-material'
import { useMemo, useState } from 'react'
import { CurrencyInput, DatePicker } from '@impres-development/assets/client'
import dayjs from 'dayjs'
import { COLUMN_MAP } from './LinesColumns'
import { useQuoteLines } from './QuoteLinesContext'
import { applyLineEdits } from './lineItemPricing'
import { useProductCategoryOptions } from './productCategories'
import { flattenBundledStructure, ManufacturerSearchAutocomplete, SupplierSearchAutocomplete } from '../../helper'

const EDITABLE_COLUMNS = Object.keys(COLUMN_MAP).filter(column => !COLUMN_MAP[column].nonEdit)

type LineEditBase = {
  onClose: () => void
  quoteId: string
}

type LineEditProps =
  | (LineEditBase & {
      mode?: 'edit'
      selectedLineItems: quoteLineType[]
    })
  | (LineEditBase & {
      mode: 'create'
      lineNumber: number
      bundle: string | null
    })

export function LineEdit(props: LineEditProps) {
  const isCreate = props.mode === 'create'
  const { lineItems, handleBulkEdit, handleAddLine, quote } = useQuoteLines()
  const productCategoryOptions = useProductCategoryOptions()
  const [updateLineItem, setUpdateLineItem] = useState<Partial<quoteLineType>>({})
  const [noChangesSnackbar, setNoChangesSnackbar] = useState(false)
  const [saveSnackbar, setSaveSnackbar] = useState<'success' | 'fail' | null>(null)
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const selectedLineItems = !isCreate ? props.selectedLineItems : []
  const selectedItem = selectedLineItems.length === 1 ? selectedLineItems[0] : undefined
  // The selection is a snapshot from when the row was ticked; read the line as it is now so grid edits show here.
  const referenceItem = useMemo(
    () =>
      selectedItem &&
      (flattenBundledStructure(lineItems).find(lineItem => lineItem.LineItemId === selectedItem.LineItemId) ??
        selectedItem),
    [lineItems, selectedItem],
  )

  const getFieldValue = (property: keyof quoteLineType) => updateLineItem[property] ?? referenceItem?.[property] ?? null

  const setFieldValue = (property: keyof quoteLineType, value: quoteLineType[keyof quoteLineType]) => {
    setUpdateLineItem(prev => ({ ...prev, [property]: value }))
  }

  const handleEditSave = async () => {
    if (Object.keys(updateLineItem).length === 0) {
      setNoChangesSnackbar(true)
      return
    }

    if (isCreate) return

    const selectedIds = new Set(props.selectedLineItems.map(item => item.LineItemId))
    const updatedLineItems = flattenBundledStructure(lineItems).map(lineItem => {
      if (!selectedIds.has(lineItem.LineItemId)) return lineItem
      return applyLineEdits(lineItem, updateLineItem)
    })

    setLoading(true)
    handleBulkEdit(updatedLineItems)
      .catch(err => {
        setSaveSnackbar('fail')
        setSnackbarMessage(err.response?.data.message ?? 'Error updating line items')
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleCreateSave = async () => {
    if (!isCreate) return

    const seed: Partial<quoteLineType> = {
      QuoteId: props.quoteId,
      LineItemId: crypto.randomUUID(),
      LineNumber: props.lineNumber + 1,
      Bundle: props.bundle,
    }
    const pricingBase = {
      ...seed,
      VendorPartNumber: null,
      Qty: 0,
      UnitCost: 0,
      UnitPrice: 0,
      TotalFees: 0,
      CreditCardFee: 0,
      PartnerFees: 0,
      ContractFees: 0,
      ExtPrice: 0,
      ExtCost: 0,
      Margin: 0,
    } as quoteLineType

    const lineItemToSave = applyLineEdits({ ...seed, ...pricingBase }, updateLineItem)

    setLoading(true)
    handleAddLine(lineItemToSave as Partial<quoteLineType>)
      .then(() => props.onClose())
      .catch(err => {
        setSaveSnackbar('fail')
        setSnackbarMessage(err.response?.data.message ?? 'Error adding line item')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleSave = () => (isCreate ? handleCreateSave() : handleEditSave())

  const renderField = (columnName: string) => {
    const columnConfig = COLUMN_MAP[columnName]
    if (!('property' in columnConfig)) return null
    const { property, format } = columnConfig
    const options = property === 'ProductCategory' ? productCategoryOptions : columnConfig.options
    const label = columnName

    /*if (format === 'boolean') {
      return (
        <FormControlLabel
          key={columnName}
          control={
            <Checkbox
              checked={(getFieldValue(property) as boolean | null) ?? false}
              onChange={e => setFieldValue(property, e.target.checked)}
            />
          }
          label={label}
        />
      )
    }*/

    if (property === 'Manufacturer') {
      return (
        <ManufacturerSearchAutocomplete
          key={columnName}
          label={label}
          fullWidth
          contractNumber={quote.ContractNumber}
          value={getFieldValue(property) as string | null}
          onChange={(value, oemCode) => {
            setFieldValue(property, value)
            setFieldValue('OEMCode', oemCode)
          }}
        />
      )
    }
    if (property === 'Provider') {
      return (
        <SupplierSearchAutocomplete
          key={columnName}
          label={label}
          fullWidth
          value={getFieldValue(property) as string | null}
          onChange={value => setFieldValue(property, value)}
        />
      )
    }

    if (format === 'currency') {
      return (
        <CurrencyInput
          key={columnName}
          size="small"
          fullWidth
          label={label}
          value={(getFieldValue(property) as number | null) ?? null}
          onChange={value => setFieldValue(property, value ?? 0)}
        />
      )
    }

    if (format === 'date') {
      const rawDate = getFieldValue(property) as string | Date | null
      return (
        <DatePicker
          key={columnName}
          label={label}
          size="small"
          value={rawDate ? dayjs(rawDate).utc() : null}
          onChange={value => setFieldValue(property, value ? dayjs(value).toDate() : null)}
        />
      )
    }

    if (format === 'percentage') {
      const rawPercent = getFieldValue(property) as number | null
      return (
        <TextField
          key={columnName}
          size="small"
          fullWidth
          type="number"
          label={label}
          value={rawPercent == null ? '' : Math.round(rawPercent * 10000) / 100}
          onChange={e => {
            const nextValue = e.target.value
            setFieldValue(property, nextValue === '' ? null : Number(nextValue) / 100)
          }}
        />
      )
    }

    if (format === 'number') {
      return (
        <TextField
          key={columnName}
          size="small"
          fullWidth
          type="number"
          label={label}
          value={getFieldValue(property) ?? ''}
          onChange={e => {
            const nextValue = e.target.value
            setFieldValue(property, nextValue === '' ? null : Number(nextValue))
          }}
        />
      )
    }

    if (options) {
      return (
        <TextField
          select
          key={columnName}
          size="small"
          fullWidth={true}
          label={label}
          value={getFieldValue(property) as string | null}
          onChange={e => setFieldValue(property, e.target.value || null)}
        >
          {options.map(option => (
            <MenuItem key={option.value as string} value={option.value as string}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )
    }

    return (
      <TextField
        key={columnName}
        size="small"
        fullWidth
        multiline={property === 'Description' || property === 'Notes'}
        label={label}
        value={(getFieldValue(property) as string | null) ?? ''}
        onChange={e => setFieldValue(property, e.target.value || null)}
      />
    )
  }

  return (
    <div className="quote-line-edit">
      <div className="quote-line-edit-header">
        <Typography variant="h6">{isCreate ? 'New Line Item' : 'Edit Quote Line Item'}</Typography>
        <IconButton onClick={() => props.onClose()}>
          <ArrowForward />
        </IconButton>
      </div>
      <Divider />
      {!isCreate && (
        <Alert severity="info" className="quote-line-edit-bulk-alert">
          <AlertTitle>
            {selectedLineItems.length} item{selectedLineItems.length > 1 ? 's' : ''} selected for editing
          </AlertTitle>
          <Typography variant="body1">
            {selectedLineItems.length > 1 ? `All ${selectedLineItems.length} items` : 'This item'} will be updated to
            reflect any changes made. Bundle headers will not be updated.
          </Typography>
        </Alert>
      )}
      <div className="quote-line-edit-content">{EDITABLE_COLUMNS.map(columnName => renderField(columnName))}</div>
      <div className="quote-line-edit-footer">
        <Button onClick={handleSave} variant="contained" color="primary" loading={loading}>
          Save
        </Button>
      </div>
      <Snackbar
        autoHideDuration={5000}
        open={noChangesSnackbar}
        onClose={() => setNoChangesSnackbar(false)}
        message="No changes to save"
      />
      <Snackbar
        autoHideDuration={5000}
        open={saveSnackbar === 'success'}
        onClose={() => setSaveSnackbar(null)}
        message={isCreate ? 'Line item added successfully' : 'Line items updated successfully'}
      />
      <Snackbar open={saveSnackbar === 'fail'} onClose={() => setSaveSnackbar(null)}>
        <Alert severity="error" variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  )
}
